import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { NumericFormat, NumberFormatValues } from "react-number-format"
import {
  hasStraySecondDecimalSeparator,
  isDecimalSeparatorPrepended,
  isFractionOnlyContinuation,
  isNoOpAfterNormalization,
  moveCaretAfterDecimal,
  normalizeNumericInputValue,
} from "utils/decimalInput"
import { getMaxWaterIntake } from "utils/waterVolumeConverter"
import {
  DetailsTrailing,
  HiddenIntakeValue,
  PlusIcon,
  StyledInput,
  StyledLabel,
  StyledTextFieldFormItem,
} from "./styled"

interface IProps {
  label: string
  value: number | null
  onChange?: (value: number) => void
  placeholder?: string
  isError?: boolean
  disabled?: boolean
  onFocus?: () => void
  onEnter?: () => void
  unit: "ml" | "oz"
  showPlusPrefix?: boolean
  setIsDisplayValueValid?: (isValid: boolean) => void
  isInModal?: boolean
  isReInitValue?: boolean
}

const WaterInput = (props: IProps) => {
  const {
    disabled = false,
    onChange,
    placeholder,
    value,
    label,
    onEnter,
    unit,
    showPlusPrefix = true,
    setIsDisplayValueValid,
    isInModal,
    isReInitValue,
  } = props

  const decimalScale = unit === "oz" ? 1 : 0
  const inputMode = unit === "oz" ? "decimal" : "numeric"
  const max = getMaxWaterIntake(unit)

  const [formattedValue, setFormattedValue] = useState("")
  const [internalValue, setInternalValue] = useState(value?.toString())
  const preventIncidentValueChangeRef = useRef(false)

  useEffect(() => {
    if (value !== Number(internalValue) || isReInitValue) {
      setInternalValue(value?.toString())
      if (isReInitValue) {
        preventIncidentValueChangeRef.current = true
        setTimeout(() => {
          preventIncidentValueChangeRef.current = false
        }, 32)
      }
    }
  }, [value])

  const inputRef = useRef<HTMLInputElement>(null)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const pendingDecimalResetRef = useRef(false)

  useEffect(() => () => clearTimeout(blurTimeoutRef.current), [])

  // After a max-clamp overwrites internalValue, NumericFormat's own
  // caret-diffing can misplace the cursor - bumping this triggers an
  // effect that forces it back to the end.
  const [cursorFixTrigger, setCursorFixTrigger] = useState(0)
  const isInitialMount = useRef(true)

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    // deferred via setTimeout so this runs after NumericFormat's own
    // value/caret handling has settled, instead of racing it
    setTimeout(() => {
      const input = inputRef.current
      if (input) {
        const end = input.value.length
        input.setSelectionRange(end, end)
      }
    }, 0)
  }, [cursorFixTrigger])

  const onBlur = () => {
    clearTimeout(blurTimeoutRef.current)
    blurTimeoutRef.current = setTimeout(() => {
      const nextValue = value ?? 0

      if (internalValue !== nextValue.toString()) {
        setInternalValue(nextValue.toString())
      }
    }, 100)
  }

  const onValueChange = (values: NumberFormatValues) => {
    if (disabled || preventIncidentValueChangeRef.current) return

    if (pendingDecimalResetRef.current) {
      pendingDecimalResetRef.current = false
      setInternalValue(".")
      setIsDisplayValueValid?.(false)
      // delayed past the onFocus select-all timer below (32ms) so this wins
      // the race instead of the caret ending up selected-all on a fresh focus
      setTimeout(() => moveCaretAfterDecimal(inputRef.current), 40)
      return
    }

    setFormattedValue(values.formattedValue)
    const normalized = normalizeNumericInputValue(values.value)

    if (normalized === "") {
      setInternalValue("")
      setIsDisplayValueValid?.(false)
      return
    }

    // captured before setInternalValue(normalized) below overwrites it, so
    // this reflects the value going into this keystroke
    const wasFractionOnlyContinuation = isFractionOnlyContinuation(
      values.value,
      internalValue ?? "",
    )

    setIsDisplayValueValid?.(true)
    setInternalValue(normalized)

    const nextValue = values.floatValue ?? 0

    // checked before the "mid-typing a decimal" bail-out below, so an edit
    // that pushes the value over max (e.g. prepending a digit into "38." to
    // get "538.") gets clamped immediately instead of sitting unvalidated
    // in the field until a further digit finally triggers this check
    if (max !== undefined && nextValue > max) {
      onChange?.(max)
      // a fraction typed right after the integer part already hit max (e.g.
      // "338." -> "338.1") snaps to "338.0" instead of bare "338", so the
      // user sees the keystroke land rather than appearing to be ignored
      setInternalValue(
        wasFractionOnlyContinuation
          ? max.toFixed(decimalScale)
          : max.toString(),
      )
      setCursorFixTrigger((k) => k + 1)
      return
    }

    // mid-typing a decimal (oz mode only) - wait for more digits before firing onChange
    if (normalized.endsWith(".")) {
      if (normalized === ".") {
        setIsDisplayValueValid?.(false)
      }
      return
    }

    if (nextValue !== value) {
      onChange?.(nextValue)
    }
  }

  // Once already clamped to max, every further digit is also over max -
  // reject those keystrokes outright so we don't remount on every one.
  // Values coming from a valid sub-max state straight to over-max (typing
  // "339" fresh, or pasting) still fall through to onValueChange's
  // clamp+remount above, since `current` won't equal `max` yet there.
  const isAllowed = (values: NumberFormatValues) => {
    pendingDecimalResetRef.current = false

    const rawValue = inputRef.current?.value ?? values.value

    if (isDecimalSeparatorPrepended(rawValue, internalValue ?? "")) {
      pendingDecimalResetRef.current = true
      return true
    }

    if (hasStraySecondDecimalSeparator(rawValue, internalValue ?? "")) {
      return false
    }

    // block outright rather than allow-then-correct, same reasoning as the
    // max-clamp desync bug: overriding an already-committed value risks the
    // DOM and state disagreeing, so reject "00"/"000"/etc. before they land
    const normalized = normalizeNumericInputValue(values.value)
    if (isNoOpAfterNormalization(values.value, normalized, internalValue ?? ""))
      return false

    if (max === undefined) return true

    const current = Number(internalValue)
    const isAlreadyAtMax = current === max
    const isOverMaxAttempt = (values.floatValue ?? 0) > max
    const isFractionContinuation = isFractionOnlyContinuation(
      values.value,
      internalValue ?? "",
    )

    // a fraction typed right after the integer part already hit max (e.g.
    // "338." -> "338.1") isn't a repeated overflow keystroke - let it fall
    // through to onValueChange's clamp-to-"max.0" handling instead of
    // rejecting it outright
    const isRepeatedOverflowKeystroke =
      isAlreadyAtMax && isOverMaxAttempt && !isFractionContinuation

    if (isRepeatedOverflowKeystroke) return false
    return true
  }

  const hiddenValueRef = useRef<HTMLDivElement>(null)
  const [valueWidth, setValueWidth] = useState(0)

  useLayoutEffect(() => {
    const width = hiddenValueRef.current?.getBoundingClientRect().width ?? 0
    setValueWidth(width)
  }, [hiddenValueRef, formattedValue])

  const trailingRef = useRef<HTMLDivElement>(null)
  const [trailingWidth, setTrailWidth] = useState(0)

  useLayoutEffect(() => {
    const width = trailingRef.current?.getBoundingClientRect().width
    const increment = isInModal ? 5 : 0
    setTrailWidth((width ?? 0) + increment)
  }, [trailingRef, unit])

  return (
    <StyledTextFieldFormItem
      style={
        {
          "--input-padding-right": "16px",
          "--input-trailing-padding-left": "6px",
          "--detail-trailing-width": `${trailingWidth}px`,
          "--value-width": `${valueWidth}px`,
        } as React.CSSProperties
      }
    >
      <StyledLabel>{label}</StyledLabel>

      <NumericFormat
        isAllowed={isAllowed}
        customInput={StyledInput}
        getInputRef={inputRef}
        inputMode={inputMode}
        $disabled={disabled}
        disabled={disabled}
        decimalScale={decimalScale}
        allowNegative={false}
        thousandSeparator=","
        decimalSeparator="."
        allowedDecimalSeparators={[",", "."]}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            setTimeout(() => {
              onEnter?.()
            }, 0)
          }
        }}
        onFocus={() => {
          setTimeout(() => {
            inputRef.current?.select()
          }, 32)
        }}
        onBlur={onBlur}
        readOnly={false}
        $isError={false}
        placeholder={placeholder}
        value={internalValue ?? ""}
        valueIsNumericString
        onValueChange={onValueChange}
        autoComplete="off"
        style={{
          paddingRight:
            "calc(var(--input-padding-right) + var(--input-trailing-padding-left) + var(--detail-trailing-width))",
        }}
      />
      {showPlusPrefix && internalValue && Number(internalValue) > 0 ? (
        <PlusIcon
          style={{
            right:
              "calc(var(--input-padding-right) + var(--input-trailing-padding-left) + var(--detail-trailing-width) + var(--value-width) + var(--input-trailing-padding-left))",
          }}
        >
          +
        </PlusIcon>
      ) : undefined}

      <DetailsTrailing ref={trailingRef}>{unit}</DetailsTrailing>
      <HiddenIntakeValue ref={hiddenValueRef}>
        {formattedValue}
      </HiddenIntakeValue>
    </StyledTextFieldFormItem>
  )
}

export default WaterInput
